import { FilterTypes, LineItem } from "@madhive/mad-sdk";
import { firstValueFrom } from "rxjs";
import { madSDK } from "lib/sdk";
import { logErrorToStackdriver } from "api/logErrors";

type TargetingCloneFields = Partial<
  Pick<
    LineItem,
    | "viewability"
    | "contextualTargeting"
    | "contextualAvoidance"
    | "keywordTargeting"
    | "keywordAvoidance"
    | "contextualSegmentIncludes"
    | "contextualSegmentExcludes"
  >
>;

/**
 * Targeting fields the legacy line item detail fetch does not carry. Only
 * defined values are spread — an empty array is the service's "clear field"
 * signal, and an undefined viewability serializes to ANY_VIEWABILITY.
 *
 * The deprecated contextual segment arrays are included because orgs without
 * IAS_KEYWORD_TARGETING still store their contextual targeting there.
 *
 * Segment ids are copied as-is, unlike the inventory packages this same
 * duplicate flow regenerates — they reference the read-only ext-segments
 * catalog rather than being owned by the line item.
 */
export const buildTargetingCloneFields = (
  source: TargetingCloneFields | null | undefined
): TargetingCloneFields =>
  !source
    ? {}
    : {
        ...(source.viewability !== undefined && {
          viewability: source.viewability
        }),
        ...(source.contextualTargeting !== undefined && {
          contextualTargeting: source.contextualTargeting
        }),
        ...(source.contextualAvoidance !== undefined && {
          contextualAvoidance: source.contextualAvoidance
        }),
        ...(source.keywordTargeting !== undefined && {
          keywordTargeting: source.keywordTargeting
        }),
        ...(source.keywordAvoidance !== undefined && {
          keywordAvoidance: source.keywordAvoidance
        }),
        ...(source.contextualSegmentIncludes !== undefined && {
          contextualSegmentIncludes: source.contextualSegmentIncludes
        }),
        ...(source.contextualSegmentExcludes !== undefined && {
          contextualSegmentExcludes: source.contextualSegmentExcludes
        })
      };

/**
 * Sources the targeting fields for a duplicate from the SDK model, since the
 * legacy detail fetch the duplicate handlers use does not carry them.
 * Enrichment only — a failure is logged and yields no fields rather than
 * blocking the duplicate.
 */
export const fetchTargetingCloneFields = async (
  lineItemId: string
): Promise<TargetingCloneFields> =>
  buildTargetingCloneFields(
    await firstValueFrom(
      madSDK.campaigns.lineItems.find_once({
        where: [{ field: "id", type: FilterTypes.EQ, value: lineItemId }]
      })
    ).catch(err => {
      logErrorToStackdriver(err as Error);
      return null;
    })
  );
